import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/api';
import SEO from '../components/SEO';

/* ── Smart description renderer ──────────────────────────────────────────────
   Parses the raw description text preserving the admin's formatting:
   - Line starting with " (quote) → Header (h2)
   - Short line (≤120 chars, no period at end or ends with —) → Subheading
   - ✔ lines → feature/benefit items
   - •/- list lines → bullet list
   - Numbered lines (1. 2. etc) → numbered list item
   - Everything else → paragraph
   First 4 paragraphs visible, rest hidden behind Read More.
────────────────────────────────────────────────────────────────────────────── */
function DescriptionRenderer({ text }) {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  // Split on newlines, filter empty lines at boundaries
  const rawLines = text.split('\n');

  // Build semantic blocks
  const blocks = [];
  rawLines.forEach((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) {
      // blank line = paragraph separator (skip, handled by spacing)
      return;
    }
    blocks.push(line);
  });

  // Classify each block
  const classified = blocks.map(line => {
    const t = line.trim();
    // Header: starts with " or contains em-dash and ≤ 120 chars and no period
    if (t.startsWith('"') && t.length <= 160) return { type: 'header', text: t };
    // Subheading: short line ending with — or — or no period, not a full paragraph
    if (t.endsWith('—') || t.endsWith('—') || (t.length <= 100 && !t.endsWith('.') && !t.startsWith('✔') && !t.match(/^\d+\./))) return { type: 'subheading', text: t };
    // Checkmark feature line
    if (t.startsWith('✔')) return { type: 'feature', text: t };
    // Bullet list
    if (t.startsWith('•') || t.startsWith('-') || t.startsWith('·')) return { type: 'bullet', text: t.replace(/^[•\-·]\s*/, '') };
    // Numbered list
    if (/^\d+\./.test(t)) return { type: 'numbered', text: t };
    // Normal paragraph
    return { type: 'paragraph', text: t };
  });

  // Identify which blocks are "main description paragraphs" (first 4)
  let paraCount = 0;
  const withIndex = classified.map(b => {
    const isVisible = b.type === 'header' || b.type === 'subheading';
    let showAlways = isVisible;
    let paraIdx = -1;
    if (b.type === 'paragraph') {
      paraIdx = paraCount++;
      showAlways = paraIdx < 4;
    }
    return { ...b, showAlways, paraIdx };
  });

  const visible = withIndex.filter(b => b.showAlways || b.type !== 'paragraph' || b.paraIdx < 4);
  const hidden  = withIndex.filter(b => b.type === 'paragraph' && b.paraIdx >= 4);
  const hasMore = hidden.length > 0;

  const renderBlock = (b, i) => {
    switch (b.type) {
      case 'header':
        return (
          <h2 key={i} className="font-serif text-xl md:text-2xl text-ink font-semibold mt-6 mb-2 leading-snug">
            {b.text}
          </h2>
        );
      case 'subheading':
        return (
          <p key={i} className="text-gold text-sm font-medium italic mb-4 leading-relaxed">
            {b.text}
          </p>
        );
      case 'paragraph':
        return (
          <p key={i} className="text-ink-400 leading-relaxed mb-4 text-[15px]">
            {b.text}
          </p>
        );
      case 'feature': {
        // ✔ Bold label: description
        const colonIdx = b.text.indexOf('\n');
        const parts = b.text.replace('✔ ', '').split('\n');
        const label = parts[0];
        const desc  = parts.slice(1).join(' ');
        return (
          <div key={i} className="flex gap-2 mb-2">
            <span className="text-gold font-bold shrink-0">✔</span>
            <div>
              <span className="font-semibold text-ink text-sm">{label}</span>
              {desc && <span className="text-ink-400 text-sm ml-1">{desc}</span>}
            </div>
          </div>
        );
      }
      case 'bullet':
        return (
          <div key={i} className="flex gap-2 mb-1.5 items-start">
            <span className="text-gold shrink-0 mt-1">•</span>
            <span className="text-ink-400 text-sm leading-relaxed">{b.text}</span>
          </div>
        );
      case 'numbered':
        return (
          <p key={i} className="text-ink-400 text-sm leading-relaxed mb-2">
            <span className="font-semibold text-ink">{b.text.match(/^\d+\./)?.[0] || ''}</span>{' '}
            {b.text.replace(/^\d+\.\s*/, '')}
          </p>
        );
      default:
        return (
          <p key={i} className="text-ink-400 leading-relaxed mb-4 text-[15px]">{b.text}</p>
        );
    }
  };

  return (
    <div className="space-y-0">
      {/* Always visible: headers, subheadings, first 4 paragraphs, features, bullets etc */}
      {withIndex.map((b, i) => {
        if (b.type === 'paragraph' && b.paraIdx >= 4 && !expanded) return null;
        return renderBlock(b, i);
      })}

      {/* Read More / Less toggle */}
      {hasMore && (
        <button
          onClick={() => setExpanded(v => !v)}
          className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-gold border border-gold px-5 py-2.5 hover:bg-gold hover:text-white transition-all duration-300 group">
          {expanded ? (
            <><ChevronUp size={14} /> Read Less</>
          ) : (
            <><ChevronDown size={14} /> Read More</>
          )}
        </button>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/products/${id}`)
      .then(res => setProduct(res.data.product))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!product) return (
    <div className="min-h-screen flex items-center justify-center text-ink-400">Product not found.</div>
  );

  const images = product.images?.length
    ? product.images
    : ['https://via.placeholder.com/800x800/EDE9DC/B8A830?text=Sample'];

  return (
    <>
      <SEO title={product.title} description={product.description?.slice(0, 160)} image={images[0]} />
      <div className="pt-24 pb-16 px-6 max-w-7xl mx-auto">
        <Link to="/products" className="flex items-center gap-2 text-ink-400 hover:text-gold text-sm mb-10 transition-colors">
          <ArrowLeft size={16} /> Back to Products
        </Link>
        <div className="grid md:grid-cols-2 gap-16">
          {/* Images */}
          <div>
            <div className="aspect-square overflow-hidden bg-dark-800 mb-4 border border-dark-600">
              <img src={images[activeImg]} alt={product.title} className="w-full h-full object-cover" />
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 flex-wrap">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`w-20 h-20 overflow-hidden border-2 transition-colors ${i === activeImg ? 'border-gold' : 'border-dark-600'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="pt-4">
            <span className="text-gold text-xs tracking-widest uppercase">
              {product.category?.replace(/-/g, ' ')}
            </span>
            <h1 className="font-serif text-3xl md:text-4xl text-ink mt-2 mb-4">{product.title}</h1>
            <div className="w-12 h-px bg-gold mb-6" />

            {/* Smart description renderer */}
            <DescriptionRenderer text={product.description} />

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6 mb-8">
                {product.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 border border-dark-600 text-ink-400 text-xs">{tag}</span>
                ))}
              </div>
            )}

            <div className="mt-8">
              <Link to="/contact" className="btn-gold">Enquire Now</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
